import { __awaiter } from "tslib";
import { get } from 'svelte/store';
import { Container, Service } from './container';
import { AppState } from '../etc/stores';
import { Auth } from './auth';
import { isMobile, notificationAudio, parseMoney, sleep } from '../etc/utils';
import { Backend } from './backend';
import { Config } from './config';
import { SessionManager } from './session-manager';
import Time from 'date-and-time';
import Analytics from './analytics';
import { usePostCheckoutModal } from '../etc/hooks/useModal';
import flags from '../etc/flagKeys';
import sessionKeys from '../etc/sessionKeys';
import { Customer } from './customer';
export class Checkout extends Service {
    constructor() {
        super(...arguments);
        this.backend = this.ctx.get(Backend);
        this.auth = this.ctx.get(Auth);
        this.config = this.ctx.get(Config);
        this.session = this.ctx.get(SessionManager);
        this.customer = this.ctx.get(Customer);
        this.softLoginEnabled = undefined;
    }
    onAppInit() {
        return __awaiter(this, void 0, void 0, function* () {
            const checkout = this.session.get(sessionKeys.CHECKOUT);
            if (checkout &&
                checkout.transactionId &&
                !this.auth.isConnectingAccount()) {
                this.checkCheckout(checkout.transactionId);
            }
            if (this.auth.provider === 'wix') {
                this.softLoginEnabled = yield this.auth.isSoftLoginEnabled();
            }
        });
    }
    handleCheckout(checkout, provider) {
        return __awaiter(this, void 0, void 0, function* () {
            let times = 0;
            while ((!get(AppState).business ||
                !get(AppState).business.id ||
                !get(AppState).settings ||
                Object.keys(get(AppState).settings).length === 0) &&
                times < 10) {
                yield sleep(1000);
                times++;
            }
            const { business, settings } = get(AppState);
            if (!business || !business.id || !settings.enabled)
                return;
            Analytics.logEvent('checkout-trigger', `${Analytics.getCategoryLoginState()}`);
            const next = yield this.getCheckoutHandlingFunction(checkout, provider);
            if (this.softLoginEnabled === undefined) {
                this.softLoginEnabled = yield this.auth.isSoftLoginEnabled();
            }
            /**
             * If it was going to open the post-checkout modal,
             * show the loading bar instead.
             * If doesn't, then just handle the checkout as usually.
             */
            if (this.softLoginEnabled && isMobile() && this.auth.provider !== 'wix') {
                AppState.update(state => (Object.assign(Object.assign({}, state), { website: Object.assign(Object.assign({}, state.website), { popoverOpen: false, showCollapsedIcon: false, category: 'postCheckoutLoading' }), checkout: Object.assign(Object.assign({}, state.checkout), { checkoutHandleFunction: next }) })));
            }
            else {
                let transaction = yield next();
                if (!transaction &&
                    get(AppState).featureFlags.includes(flags.PROVISIONAL_CHECKOUT_WIX)) {
                    this.session.set(sessionKeys.CHECKOUT_WITHOUT_TRANSACTION, Object.assign({ transactionId: checkout.externalTransactionId, createdAt: new Date() }, checkout), {
                        expires: Time.addHours(new Date(), 3)
                    });
                    Analytics.logEvent('auto-coins-trigger', `provisional-${Analytics.getCategoryLoginState()}`);
                    notificationAudio.play();
                    AppState.update(state => (Object.assign(Object.assign({}, state), { checkout, website: Object.assign(Object.assign({}, state.website), { checkout: true, coins: parseMoney(checkout.coinsAmount), spent: parseMoney(checkout.value), category: get(AppState).auth.connected
                                ? 'youGotCoins'
                                : 'provisionalCoins' }) })));
                }
                else {
                    yield this.onCheckoutSuccess(transaction);
                }
            }
        });
    }
    verifyProvisionalCheckoutWix() {
        return __awaiter(this, void 0, void 0, function* () {
            const checkout = this.session.get(sessionKeys.CHECKOUT_WITHOUT_TRANSACTION);
            if (checkout) {
                const { transaction } = yield this.backend.getTransactionLoyalty({
                    provider: this.auth.provider,
                    transactionId: checkout.transactionId
                });
                if (transaction) {
                    AppState.update(state => (Object.assign(Object.assign({}, state), { website: Object.assign(Object.assign({}, state.website), { showNotificationTransaction: true }) })));
                    yield this.customer.loadState();
                    this.session.remove(sessionKeys.CHECKOUT_WITHOUT_TRANSACTION);
                }
            }
        });
    }
    getCheckoutHandlingFunction(checkout, provider) {
        return __awaiter(this, void 0, void 0, function* () {
            const { items: featureFlags } = yield this.backend.getFeatureFlags();
            const isProvisionalCheckoutEnabled = featureFlags.includes(flags.PROVISIONAL_CHECKOUT_FLAG_SLUG);
            if (isProvisionalCheckoutEnabled &&
                !['wix', 'shopify'].includes(provider)) {
                return () => this.createProvisionalCheckout(checkout);
            }
            return () => this.checkCheckout(checkout.transactionId);
        });
    }
    createProvisionalCheckout(checkout) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!checkout.externalTransactionId) {
                return;
            }
            const { provider, account } = this.config.getSiteSettings();
            const transaction = yield this.backend.createProvisionalTransactional(Object.assign({ account }, checkout), provider);
            if (transaction) {
                if (!transaction.contactFirstName)
                    transaction.contactFirstName = checkout.externalCustomerFirstName;
                if (!transaction.contactLastName)
                    transaction.contactLastName = checkout.externalCustomerLastName;
                if (transaction.transactionToken) {
                    this.session.set(sessionKeys.TRANSACTION_TOKEN, transaction.transactionToken, {
                        expires: Time.addHours(new Date(), 1)
                    });
                }
                return transaction;
            }
        });
    }
    checkCheckout(transactionId) {
        return __awaiter(this, void 0, void 0, function* () {
            const { provider } = this.config.getSiteSettings();
            let tries = 0;
            let time = 1000;
            do {
                const { transaction } = yield this.backend.getTransactionLoyalty({
                    provider,
                    transactionId
                });
                if (transaction) {
                    if (transaction.transactionToken) {
                        this.session.set(sessionKeys.TRANSACTION_TOKEN, transaction.transactionToken, {
                            expires: Time.addHours(new Date(), 1)
                        });
                    }
                    return transaction;
                }
                tries++;
                yield sleep(time);
            } while (tries < 10);
        });
    }
    onCheckoutSuccess(checkout) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!checkout || !checkout.coinsAmount)
                return;
            const { customer, moneyAmount, coinsAmount } = checkout;
            // Avoids that the popover for "you got coins" keep showing on every page refresh
            const showPopover = this.session.get(sessionKeys.LAST_CHECKOUT_ID) !==
                checkout.externalTransactionId;
            if (!showPopover) {
                this.session.remove(sessionKeys.CHECKOUT);
                return;
            }
            const state = get(AppState);
            let isRedirectSoftLogin = this.softLoginEnabled && !state.auth.connected;
            const isSoftLoggedIn = state.auth.connected && !state.auth.token;
            if (this.softLoginEnabled) {
                isRedirectSoftLogin = isRedirectSoftLogin && !isSoftLoggedIn;
            }
            if (customer &&
                customer.masked &&
                (!this.softLoginEnabled || !isSoftLoggedIn)) {
                AppState.update(state => (Object.assign(Object.assign({}, state), { website: Object.assign(Object.assign({}, state.website), { checkout: true, coins: parseMoney(coinsAmount), spent: parseMoney(moneyAmount), category: isMobile()
                            ? 'creditCardGotCoinsMobile'
                            : 'creditCardGotCoins', requestEmailVerification: false }) })));
                return;
            }
            if (customer && this.auth.provider !== 'shopify') {
                const auth = Container.get(Auth);
                auth.loadState();
            }
            const checkoutData = {
                checkout,
                website: {
                    checkout: true,
                    popoverOpen: false,
                    coins: parseMoney(coinsAmount),
                    spent: parseMoney(moneyAmount),
                    requestEmailVerification: false
                }
            };
            AppState.update(state => (Object.assign(Object.assign({}, state), { checkout, website: Object.assign(Object.assign(Object.assign({}, state.website), checkoutData.website), { connectingAccount: false }) })));
            if (isRedirectSoftLogin) {
                this.session.set(sessionKeys.CHECKOUT_SOFT_LOGIN_MODAL, checkoutData);
                usePostCheckoutModal().open();
            }
            else {
                if (state.auth.connected)
                    yield this.customer.loadState();
                AppState.update(state => (Object.assign(Object.assign({}, state), { website: Object.assign(Object.assign({}, state.website), { category: 'youGotCoins' }) })));
                notificationAudio.play();
            }
            Analytics.logEvent('auto-coins-trigger', `${isRedirectSoftLogin ? 'soft-signup' : 'rewards'}-${Analytics.getCategoryLoginState()}`);
            this.session.set(sessionKeys.LAST_CHECKOUT_ID, checkout.externalTransactionId, {
                expires: Time.addHours(new Date(), 1)
            });
            this.session.set(sessionKeys.CHECKOUT, Object.assign({ transactionId: checkout.externalTransactionId }, checkout), {
                expires: Time.addHours(new Date(), 1)
            });
        });
    }
}
Container.add(Checkout);
